import { useState } from "react";
import { Plus, Trash2, Paperclip } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { templates, defaultChipIds, MAX_CHIPS, KIND_LABEL } from "@/pages/dashboard/copilot/templates";

// Settings → Capabilities → "Ask Copilot starters": which chips the welcome screen shows, whether
// the "All starters" link shows, and the community's own starters. Saves through the tenant
// config autosave (`copilot_starters`).
export function CopilotStartersCard({ value, onChange, readOnly }) {
  const cfg = value || {};
  const ids = cfg.ids && cfg.ids.length ? cfg.ids : defaultChipIds();
  const custom = cfg.custom || [];
  const library = cfg.library !== false;
  const [draft, setDraft] = useState({ label: "", prompt: "" });
  const set = (patch) => onChange({ ids, library, custom, ...patch });
  const toggle = (id) => {
    if (readOnly) return;
    if (ids.includes(id)) return set({ ids: ids.filter((x) => x !== id) });
    if (ids.length >= MAX_CHIPS) return;
    set({ ids: [...ids, id] });
  };
  const addCustom = () => {
    const label = draft.label.trim(), prompt = draft.prompt.trim();
    if (!label || !prompt) return;
    const id = `cs_${Date.now().toString(36)}`;
    set({ custom: [...custom, { id, label, prompt }], ids: ids.length < MAX_CHIPS ? [...ids, id] : ids });
    setDraft({ label: "", prompt: "" });
  };
  const removeCustom = (id) => set({ custom: custom.filter((c) => c.id !== id), ids: ids.filter((x) => x !== id) });
  const all = [...templates(), ...custom.map((c) => ({ ...c, kind: "custom" }))];

  return (
    <div className="space-y-4" data-testid="copilot-starters-card">
      <p className="text-sm text-muted-foreground -mt-1">
        The welcome screen shows a few quiet suggestion chips. Pick up to {MAX_CHIPS}; the rest stay one click away under
        <span className="font-semibold text-ink"> All starters</span>. Add your own for the letters and questions your team writes every week.
      </p>
      <div className="flex items-center justify-between gap-4 rounded-2xl border border-sand-200 bg-sand-50/60 px-4 py-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm">Show the “All starters” link</div>
          <div className="text-xs text-muted-foreground">Off = only the chips you pick below.</div>
        </div>
        <Switch data-testid="copilot-starters-library" disabled={readOnly} checked={library} onCheckedChange={(v) => set({ library: v })} />
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground mb-2">
          Chips on the welcome screen · <span data-testid="copilot-starters-count">{ids.length}/{MAX_CHIPS}</span>
        </div>
        {["ask", "draft", "analyze", "custom"].map((k) => {
          const rows = all.filter((t) => t.kind === k);
          if (!rows.length) return null;
          return (
            <div key={k} className="mb-2.5">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink/40 mb-1">{KIND_LABEL[k]}</div>
              <div className="flex flex-wrap gap-1.5">
                {rows.map((t) => {
                  const on = ids.includes(t.id);
                  const full = !on && ids.length >= MAX_CHIPS;
                  return (
                    <button key={t.id} data-testid={`copilot-starter-pick-${t.id}`} onClick={() => toggle(t.id)} disabled={readOnly || full} aria-pressed={on} title={t.prompt}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 ${
                        on ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-sand-200 hover:border-ink/40"}`}>
                      {t.label}{t.needsFile && <Paperclip className="h-3 w-3 opacity-60" />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-sand-200 p-4">
        <div className="font-semibold text-sm mb-1">Your own starters</div>
        <div className="text-xs text-muted-foreground mb-3">A label for the chip and the exact prompt it sends. Use [brackets] for the blanks the manager fills in.</div>
        {custom.length > 0 && (
          <div className="space-y-1.5 mb-3" data-testid="copilot-starters-custom-list">
            {custom.map((c) => (
              <div key={c.id} data-testid={`copilot-custom-${c.id}`} className="flex items-start gap-3 rounded-xl bg-sand-50 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">{c.label}</div>
                  <div className="text-xs text-ink/60 line-clamp-2">{c.prompt}</div>
                </div>
                {!readOnly && (
                  <button data-testid={`copilot-custom-remove-${c.id}`} onClick={() => removeCustom(c.id)} className="p-1 text-ink/35 hover:text-danger-600 transition-colors" aria-label="Remove starter">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!readOnly && (
          <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
            <input data-testid="copilot-custom-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Chip label — e.g. Pool key request"
              className="rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink/40" />
            <input data-testid="copilot-custom-prompt" value={draft.prompt} onChange={(e) => setDraft({ ...draft, prompt: e.target.value })} placeholder="Prompt — Draft a reply to [name] confirming their pool key is ready…"
              className="rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink/40" />
            <button data-testid="copilot-custom-add" onClick={addCustom} disabled={!draft.label.trim() || !draft.prompt.trim()}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-ink text-white px-3.5 py-2 text-sm font-semibold hover:bg-ink-700 disabled:opacity-40 transition-colors">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
